import React from 'react';
import { 
    List, 
    Datagrid, 
    TextField, 
    EmailField,
    BooleanField,
    DateField,
    Edit,
    SimpleForm,
    BooleanInput,
    Create,
    required,
    email,
    Show,
    SimpleShowLayout,
    FunctionField,
    usePermissions,
    Button,
    TopToolbar,
    CreateButton,
    useNotify,
    Pagination,
    useRecordContext,
    ShowButton,
    EditButton,
} from 'react-admin';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import LockResetIcon from '@mui/icons-material/LockReset';
import { API_URL } from '../config';
import ChangePasswordDialog from '../components/ChangePasswordDialog';
import {
    JumpstartTextInput,
    JumpstartPasswordInput,
    JumpstartCheckboxInput,
    JumpstartFormToolbar,
    JumpstartSearchInput,
} from '../components/JumpstartInputs';
import { useT } from '../i18n/useT';

// Change Password Button for admin changing other user's password
const ChangePasswordAdminButton = ({ record }: { record: any }) => {
    const [open, setOpen] = React.useState(false);
    const { permissions } = usePermissions();

    if (permissions !== 'admin') {
        return null;
    }

    return (
        <>
            <Button
                onClick={() => setOpen(true)}
                label="resources.users.actions.changePassword"
                startIcon={<LockResetIcon />}
                color="warning"
            />
            <ChangePasswordDialog
                open={open}
                onClose={() => setOpen(false)}
                userId={record?.id}
                userName={record?.full_name || record?.email}
            />
        </>
    );
};

const ShowChangePasswordAction = () => {
    const record = useRecordContext();
    const { permissions } = usePermissions();

    if (permissions !== 'admin' || !record) {
        return null;
    }

    return <ChangePasswordAdminButton record={record} />;
};

// Export users button
const ExportUsersButton = () => {
    const notify = useNotify();
    const t = useT();

    const handleExport = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                notify(t('notifications.authRequired'), { type: 'error' });
                return;
            }

            // Fetch all users
            const response = await fetch(`${API_URL}/auth/users?skip=0&limit=10000`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error('Failed to fetch users');
            }

            const users = await response.json();

            // Create CSV content
            const csvContent = [
                ['ID', 'Email', 'Full Name', 'Is Active', 'Is Admin', 'Created At', 'Updated At'],
                ...users.map((user: any) => [
                    user.id,
                    user.email,
                    user.full_name || '',
                    user.is_active ? 'Yes' : 'No',
                    user.is_admin ? 'Yes' : 'No',
                    user.created_at,
                    user.updated_at,
                ]),
            ]
                .map((row: any[]) => row.map((cell: any) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
                .join('\n');

            // Create and download CSV file
            const blob = new Blob([csvContent], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `users_${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            notify(t('resources.users.notifications.exportSuccess'), { type: 'success' });
        } catch (error) {
            console.error('Export error:', error);
            notify(t('resources.users.notifications.exportFailed'), { type: 'error' });
        }
    };

    return (
        <Button
            label="actions.exportCsv"
            onClick={handleExport}
            startIcon={<FileDownloadIcon />}
        />
    );
};

// List actions toolbar
const UserListActions = () => {
    const { permissions } = usePermissions();
    return (
        <TopToolbar>
            <ExportUsersButton />
            {permissions === 'admin' && <CreateButton />}
        </TopToolbar>
    );
};

// List of users with filtering capabilities
export const UserList = () => {
    const { permissions } = usePermissions();
    const t = useT();
    
    return (
        <List 
            actions={<UserListActions />}
            perPage={25}
            pagination={<Pagination rowsPerPageOptions={[10, 25, 50, 100]} />}
            filters={[
                <JumpstartSearchInput key="email" source="email" placeholder={t('resources.users.filters.emailPlaceholder')} alwaysOn />,
                <BooleanInput key="is_active" source="is_active" />,
                ...(permissions === 'admin' ? [
                    <BooleanInput key="is_admin" source="is_admin" />
                ] : []),
            ]}
            empty={<div style={{padding: '1rem', textAlign: 'center'}}>
                <h3>{t('resources.users.empty.title')}</h3>
                <p>{t('resources.users.empty.hint')}</p>
            </div>}
        >
            <Datagrid rowClick={false}>
                <TextField source="id" />
                <EmailField source="email" />
                <TextField source="full_name" />
                <BooleanField source="is_active" />
                {permissions === 'admin' && (
                    <BooleanField source="is_admin" />
                )}
                <DateField source="created_at" />
                <DateField source="updated_at" />
                <ShowButton />
                {permissions === 'admin' && <EditButton />}
            </Datagrid>
        </List>
    );
};

// Edit form for users
export const UserEdit = () => {
    const { permissions } = usePermissions();
    const t = useT();
    
    return (
        <Edit>
            <SimpleForm toolbar={<JumpstartFormToolbar />}>
                <JumpstartTextInput source="id" disabled />
                <JumpstartTextInput source="email" validate={[required(), email()]} />
                <JumpstartTextInput source="full_name" />
                <JumpstartCheckboxInput source="is_active" />
                {permissions === 'admin' && (
                    <JumpstartCheckboxInput source="is_admin" />
                )}
                <JumpstartPasswordInput source="password" helperText={t('resources.users.helpers.passwordKeepCurrent')} />
            </SimpleForm>
        </Edit>
    );
};

// Create form for users (admin only)
export const UserCreate = () => {
    const t = useT();
    return (
        <Create>
            <SimpleForm toolbar={<JumpstartFormToolbar />}>
                <JumpstartTextInput source="email" validate={[required(), email()]} />
                <JumpstartTextInput source="full_name" />
                <JumpstartCheckboxInput source="is_active" defaultValue={true} />
                <JumpstartCheckboxInput source="is_admin" defaultValue={false} />
                <JumpstartPasswordInput source="password" validate={required()} />
                <JumpstartTextInput source="invite_code" validate={required()} helperText={t('resources.users.helpers.inviteCode')} />
            </SimpleForm>
        </Create>
    );
};

// Detailed view of a user
export const UserShow = () => {
    const { permissions } = usePermissions();
    const t = useT();
    
    return (
        <Show>
            <SimpleShowLayout>
                <TextField source="id" />
                <EmailField source="email" />
                <TextField source="full_name" />
                <BooleanField source="is_active" />
                {permissions === 'admin' && (
                    <BooleanField source="is_admin" />
                )}
                <DateField source="created_at" />
                <DateField source="updated_at" />
                <FunctionField
                    source="role"
                    render={(record: any) => record.is_admin ? t('resources.users.roles.admin') : t('resources.users.roles.user')} 
                />
            </SimpleShowLayout>
            <ShowChangePasswordAction />
        </Show>
    );
};
